/**
 * useCreateRole Hook
 *
 * React Query mutation for creating new global roles with permissions.
 * Permissions are validated against existing applications.
 * Invalidates roles cache on success.
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { roleService } from '../../../api/role.service';
import type { RoleSanitized, RoleCreateDTO } from '../../../types/role.types';

/**
 * Create a new global role mutation
 *
 * @param tenantId - Tenant ID
 * @returns Mutation result
 */
export const useCreateRole = (
  tenantId: string
): UseMutationResult<RoleSanitized, Error, RoleCreateDTO> => {
  const queryClient = useQueryClient();

  return useMutation<RoleSanitized, Error, RoleCreateDTO>({
    mutationFn: (data) => roleService.createRole(tenantId, data),
    onSuccess: () => {
      // Invalidate all roles queries for this tenant
      queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });
    },
    onError: (error) => {
      console.error('Failed to create role:', error);
    },
  });
};
